/**
 * 浏览域查询 hooks(TanStack Query)。基座 queryClient 已配 retry:1/staleTime 30s。
 *  - feed / search:useInfiniteQuery(cursor / page)。
 *  - detail / reviews:useQuery,业务码不重试、非业务码重试 1 次(product-detail.md)。
 */
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ServerError } from '@/api/client';
import {
  getCategories,
  getHomeFeed,
  getProductDetail,
  getProductReviews,
  searchProducts,
  type ProductFeedResponse,
  type ProductSearchResponse,
} from '@/api/guide';

const FEED_SIZE = 20;

/**
 * cursor 分页的下一页游标。三重终止防无限拉空页(home.md / category-detail.md):
 * 无 hasMore ∨ 无 cursor ∨ 本页 items 为空 → 结束。
 */
export function feedNextCursor(last: ProductFeedResponse): string | undefined {
  if (!last.hasMore) return undefined;
  if (!last.cursor) return undefined;
  if (!last.items || last.items.length === 0) return undefined;
  return last.cursor;
}

/** 首页 / 品类专题推荐流(cursor)。categoryL1Id 进 queryKey。 */
export function useProductFeed(categoryL1Id?: string) {
  return useInfiniteQuery({
    queryKey: ['product-feed', categoryL1Id ?? null],
    queryFn: ({ pageParam }) =>
      getHomeFeed({ cursor: pageParam, size: FEED_SIZE, categoryL1Id }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: feedNextCursor,
  });
}

/** 关键词搜索(page)。空 q → enabled=false(不发请求)。 */
export function useSearch(q: string, categoryL1Id?: string) {
  const term = q.trim();
  return useInfiniteQuery({
    queryKey: ['search', term, categoryL1Id ?? null],
    queryFn: ({ pageParam }) =>
      searchProducts({ q: term, page: pageParam, size: FEED_SIZE, categoryL1Id }),
    initialPageParam: 1,
    getNextPageParam: (last: ProductSearchResponse, _allPages, lastParam: number) =>
      last.hasMore ? lastParam + 1 : undefined,
    enabled: term.length > 0,
  });
}

/** 品类树(缓存 5min;home/search/category 共用)。 */
export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
    staleTime: 5 * 60_000,
  });
}

/** 业务码不重试,非业务码(网络等)重试 1 次。 */
function nonBusinessRetry(failureCount: number, err: unknown): boolean {
  if (err instanceof ServerError) return false;
  return failureCount < 1;
}

/** 商品详情。id 缺失 → enabled=false(页面走「链接无效」)。 */
export function useProductDetail(id?: string) {
  return useQuery({
    queryKey: ['product-detail', id],
    queryFn: () => getProductDetail(id as string),
    enabled: !!id,
    retry: nonBusinessRetry,
  });
}

/** 商品评价预览(首屏社会证明)。 */
export function useProductReviews(id?: string) {
  return useQuery({
    queryKey: ['product-reviews', id],
    queryFn: () => getProductReviews(id as string),
    enabled: !!id,
    retry: nonBusinessRetry,
  });
}
